import React, { useState, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VehicleDropdowns } from 'site-modules/shared/components/vehicle-dropdowns/vehicle-dropdowns';
import { ALLOWED_PUB_STATES } from 'site-modules/shared/constants/appraisal/appraisal';
import { APPRAISER_HIGHLIGHTS_CONTENT_FILE_PATH } from 'site-modules/shared/constants/appraiser-highlights';
import {
  MAKE_INPUT,
  MODEL_INPUT,
  YEAR_INPUT,
  STYLE_INPUT,
} from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { ZipField, THEME } from 'site-modules/shared/components/appraisal/zip-field/zip-field';
import { VehicleVinModel, buildStylesBasicPathFromVin } from 'client/data/models/vehicle-vin';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { useTradeInAppraiserContext } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-context';

import {
  useStepLoadingHandler,
  useLocationAndStyleStepStorage,
  useAreStylesReady,
} from './location-and-style-step-hooks';
import { VEHICLE_DROPDOWNS_TRACKING } from './location-and-style-step-constants';
import { LocationAndStyleStepContinueBtn } from './location-and-style-step-continue-btn';
import './location-and-style-step.scss';

export const LocationAndStyleStepUI = ({
  isZipCodeValid,
  isExtendedReport = false,
  isMobile = false,
  zipCode = '',
  styleIds = '',
  estimatedVehicle = null,
  errorMap = new Map(),
  isLoadingColorsAndOptions = false,
  vin = '',
  vinStyles = null,
  displayTipForTruckType = false,
  isStepActive = false,
  params = {},
  highlightsContent = DEFAULT_CONTENT,
  isHighlightsPage = false,
}) => {
  const {
    changeZipCode: onZipCodeChange,
    setGeneralVehicleData: onDropdownsSelect,
    setAdditionalStepsData: updateStepsState,
    changeStep,
    checkVinMismatchError,
  } = useTradeInAppraiserContext();
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const isLoaded = useStepLoadingHandler(isExtendedReport);
  const storage = useLocationAndStyleStepStorage();
  const { ctaText } = highlightsContent.child('highlights').child('highlights-1').getAllMetadata();
  const areStylesReady = useAreStylesReady({ params });

  const onSelect = useCallback(
    (make, model, year, style = {}, isSetVehicleDataForExtendedReport) => {
      setSelectedVehicle({ ...style, make, model, year: year?.year, id: style?.id, slug: style?.slug });
      onDropdownsSelect(make, model, year, style, isSetVehicleDataForExtendedReport, () => {
        if (vin) {
          checkVinMismatchError(vinStyles?.results);
        }
      });
    },
    [onDropdownsSelect, vin, vinStyles, checkVinMismatchError]
  );

  const handleOnClick = useCallback(
    e => {
      const { currentTarget } = e;
      delayHeavyTask(() => {
        changeStep({ currentTarget });
      });
    },
    [changeStep]
  );

  const styleDropdownId = `style-sel-${useId()}`;
  const zipId = `zip-${useId()}`;
  const hasVehicleData = !!estimatedVehicle;
  const errorMake = errorMap.get(MAKE_INPUT);
  const errorModel = errorMap.get(MODEL_INPUT);
  const errorYear = errorMap.get(YEAR_INPUT);
  const errorStyle = errorMap.get(STYLE_INPUT);

  const vehicleDropdownRender = useCallback(
    props => (
      <Row>
        <Col xs={12} className="px-0">
          <VehicleDropdowns
            tag="div"
            showAllSubmodels={hasVehicleData}
            hasModel
            hasYear
            hasStyle
            vehicle={selectedVehicle || estimatedVehicle}
            selectStyle={!!selectedVehicle?.slug}
            hasOptionTruncate={false}
            inputClasses="form-control pe-2 w-100 mmy-select input-content"
            makeLabel="Make"
            modelLabel="Model"
            yearLabel="Year"
            styleLabel="Style"
            labelClassName="small mb-0_25"
            buttonGroupClassNames="d-none"
            errorMake={errorMake}
            errorModel={errorModel}
            errorYear={errorYear}
            errorStyle={errorStyle}
            pubStatesYears={ALLOWED_PUB_STATES}
            isStyleIdQueryParamEnabled
            isMobile={isMobile}
            updateStepsState={updateStepsState}
            disabled={!isLoaded}
            styleIds={styleIds}
            enableSpinner
            displayTipForTruckType={displayTipForTruckType}
            required={{
              make: true,
              model: true,
              year: true,
              style: true,
            }}
            hasLiveRegions
            tracking={VEHICLE_DROPDOWNS_TRACKING}
            storage={storage}
            params={params}
            {...props}
          />
        </Col>
      </Row>
    ),
    [
      displayTipForTruckType,
      errorMake,
      errorModel,
      errorStyle,
      errorYear,
      estimatedVehicle,
      hasVehicleData,
      isLoaded,
      isMobile,
      params,
      selectedVehicle,
      storage,
      styleIds,
      updateStepsState,
    ]
  );

  const renderExtendedLayout = useCallback(
    () =>
      isMobile ? (
        <>
          {vehicleDropdownRender({
            makeDropdownClassNames: 'custom-select-arrow has-right-padding mb-1 p-0 pe-md-0_75 pe-lg-1',
            modelDropdownClassNames: 'custom-select-arrow mb-1 p-0 ps-md-0_75 ps-lg-1',
            submodelDropdownClassNames: 'custom-select-arrow mb-1 p-0 ps-md-0_75 ps-lg-1',
            yearDropdownClassNames: 'custom-select-arrow has-right-padding mb-1 mb-md-0 p-0 pe-md-0_75 pe-lg-1',
            styleDropdownClassNames: 'custom-select-arrow mb-0 p-0 ps-lg-1 ps-md-0_75 pos-r',
            onDropdownsSelect: onSelect,
            showFindStyleLink: true,
            styleDropdownId,
          })}
          <Row>
            <Col
              xs={4}
              md={6}
              className={classNames('px-0 mt-md-3', {
                'mt-2': !isMobile,
                'mt-0_25': isMobile,
              })}
            >
              <ZipField
                id={zipId}
                theme={THEME.LOCATION_ICON}
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-0_75"
                errorMap={errorMap}
                disabled={!isLoaded}
                isMobile
              />
            </Col>
            <Col xs={7} md={6} lg={4} className="px-0 mt-md-3 ms-auto ps-md-0_75 mt-1_25">
              <LocationAndStyleStepContinueBtn
                onClick={handleOnClick}
                isLoaded={isLoaded}
                isLoadingColorsAndOptions={isLoadingColorsAndOptions}
                ctaText={isHighlightsPage ? ctaText : 'Continue'}
                areStylesReady={areStylesReady}
              />
            </Col>
          </Row>
        </>
      ) : (
        <>
          {vehicleDropdownRender({
            errorMessageClassNames: 'mt-0_5',
            makeDropdownClassNames: 'has-right-padding mb-1 p-0 pe-md-0_75 pe-lg-1',
            modelDropdownClassNames: 'mb-1 p-0 ps-md-0_75 ps-lg-1',
            submodelDropdownClassNames: 'mb-1 p-0 ps-md-0_75 ps-lg-1',
            yearDropdownClassNames: 'has-right-padding mb-1 mb-md-0 p-0 pe-md-0_75 pe-lg-1',
            styleDropdownClassNames: 'mb-0 p-0 ps-lg-1 ps-md-0_75 pos-r style-dropdown-error-gutter',
            isStyledSelects: true,
            onDropdownsSelect: onSelect,
            showFindStyleLink: true,
            styleDropdownId,
          })}
          <Row>
            <Col xs={12} md={6} className="px-0 mt-1">
              <ZipField
                id={zipId}
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-1 mb-0 mmy-zip-code"
                errorMap={errorMap}
                disabled={!isLoaded}
              />
            </Col>
          </Row>
        </>
      ),
    [
      areStylesReady,
      ctaText,
      errorMap,
      handleOnClick,
      isHighlightsPage,
      isLoaded,
      isLoadingColorsAndOptions,
      isMobile,
      isZipCodeValid,
      onSelect,
      onZipCodeChange,
      styleDropdownId,
      vehicleDropdownRender,
      zipCode,
      zipId,
    ]
  );

  if (isExtendedReport && !isStepActive) return null;

  return (
    <div className="location-and-style-step">
      {isExtendedReport ? (
        renderExtendedLayout()
      ) : (
        <>
          <Row>
            <Col xs={12} md={6} className="px-0">
              <ZipField
                isZipCodeValid={isZipCodeValid}
                onZipCodeChange={onZipCodeChange}
                zipCode={zipCode}
                zipClassName="pe-md-1"
                errorMap={errorMap}
                disabled={!isLoaded}
                zipWrapperClassName="mb-1"
              />
            </Col>
          </Row>
          {vehicleDropdownRender({
            makeDropdownClassNames: 'has-right-padding mb-1 p-0 pe-md-0_75 pe-lg-1',
            modelDropdownClassNames: 'mb-1 p-0 ps-md-0_75 ps-lg-1',
            submodelDropdownClassNames: 'mb-1 p-0 ps-md-0_75 ps-lg-1',
            yearDropdownClassNames: 'has-right-padding mb-1 mb-md-0 p-0 pe-md-0_75 pe-lg-1',
            styleDropdownClassNames: 'mb-0 p-0 ps-lg-1 ps-md-0_75 pos-r',
            isStyledSelects: true,
            onDropdownsSelect,
          })}
        </>
      )}
    </div>
  );
};

LocationAndStyleStepUI.propTypes = {
  isZipCodeValid: PropTypes.bool.isRequired,
  isExtendedReport: PropTypes.bool,
  isMobile: PropTypes.bool,
  zipCode: PropTypes.string,
  styleIds: PropTypes.string,
  estimatedVehicle: VehicleEntities.StyleWithoutSubmodels,
  errorMap: PropTypes.objectOf(PropTypes.string),
  isLoadingColorsAndOptions: PropTypes.bool,
  vehicle: PropTypes.shape({}),
  vin: PropTypes.string,
  vinStyles: PropTypes.shape({}),
  displayTipForTruckType: PropTypes.bool,
  isStepActive: PropTypes.bool,
  params: PropTypes.shape({
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
    styleId: PropTypes.string,
  }),
  highlightsContent: CmsEntities.Content,
  isHighlightsPage: PropTypes.bool,
};

export const stateToPropsConfig = {
  vinStyles: bindToPath(props => buildStylesBasicPathFromVin(props), VehicleVinModel),
  highlightsContent: bindToContent(({ isHighlightsPage }) =>
    isHighlightsPage ? APPRAISER_HIGHLIGHTS_CONTENT_FILE_PATH : ''
  ),
};

export const LocationAndStyleStep = connectToModel(LocationAndStyleStepUI, stateToPropsConfig);
